import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/i18n";
import { useLoginWall } from "@/components/LoginWall";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Lock, MessageCircle, Radio, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";

const GUEST_LIMIT = 3;

export default function TeammatesPage() {
  const { t } = useT();
  const { user } = useAuth();
  const { requireAuth } = useLoginWall();
  const isGuest = !user;

  const [teammates, setTeammates] = useState([]);
  const [games, setGames] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    game: "any",
    country: "any",
    language: "any",
    platform: "any",
    mode: "any",
    online_only: false,
    mic_only: false,
  });

  useEffect(() => {
    (async () => {
      const [g, c, l, p] = await Promise.all([
        api.get("/games"),
        api.get("/meta/countries"),
        api.get("/meta/languages"),
        api.get("/meta/platforms"),
      ]);
      setGames(g.data.items);
      setCountries(c.data.items);
      setLanguages(l.data.items);
      setPlatforms(p.data.items);
    })();
  }, []);

  useEffect(() => {
    fetchTeammates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchTeammates = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "any" && v !== false) params[k] = v;
      });
      const res = await api.get("/teammates", { params });
      setTeammates(res.data.items);
    } catch (e) {
      toast.error("Could not fetch teammates");
    } finally {
      setLoading(false);
    }
  };

  const onlineCount = useMemo(() => teammates.filter((t) => !!t.online).length, [teammates]);
  const visibleTeammates = isGuest ? teammates.slice(0, GUEST_LIMIT) : teammates;
  const hiddenCount = isGuest ? Math.max(0, teammates.length - GUEST_LIMIT) : 0;

  const { supabaseUserId } = useSupabaseSession();

  const handleInvite = async (tm) => {
    if (isGuest) {
      requireAuth({
        feature: "teammates",
        title: t("wall.title"),
        sub: t("guest.teammates_locked_sub"),
      });
      return;
    }
    if (!supabase || !supabaseUserId) {
      toast.error("Sitzung wird vorbereitet — bitte gleich erneut versuchen.");
      return;
    }
    try {
      // Resolve target's Supabase UUID via their nickname (uniqueness enforced).
      const { data: prof, error: pErr } = await supabase
        .from("profiles")
        .select("user_id, username")
        .ilike("username", tm.nickname)
        .limit(1)
        .maybeSingle();
      if (pErr) throw pErr;
      if (!prof?.user_id) {
        toast.error("Pilot nicht gefunden.");
        return;
      }
      // Avoid duplicates: skip if a pending invite to this teammate already exists.
      const { data: dup } = await supabase
        .from("squad_invites")
        .select("id")
        .eq("inviter_id", supabaseUserId)
        .eq("invitee_id", prof.user_id)
        .eq("status", "pending")
        .limit(1);
      if (dup && dup.length) {
        toast.info("Du hast bereits eine offene Anfrage an diesen Piloten.");
        return;
      }
      const { error: iErr } = await supabase.from("squad_invites").insert({
        inviter_id: supabaseUserId,
        invitee_id: prof.user_id,
        status: "pending",
        message: null,
      });
      if (iErr) throw iErr;
      // Fire the backend notification fanout so the invitee gets a bell-ping.
      // Privacy: do NOT pass any client-side identity (email/nickname) — the
      // backend resolves the actor's display name from Mongo authoritatively.
      try {
        await api.post("/squads/notify", {
          invitee_user_id: prof.user_id,
          kind: "squad_invite",
        });
      } catch (_e) { /* notification is best-effort */ }
      toast.success(`${t("page.teammates.invite_sent")} @${tm.nickname}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("invite failed", err);
      toast.error("Einladung konnte nicht gesendet werden.");
    }
  };

  const handleFilterChange = (patch) => {
    if (isGuest) {
      // Guests still get to feel filters work (no wall).
      setFilters((f) => ({ ...f, ...patch }));
      return;
    }
    setFilters((f) => ({ ...f, ...patch }));
  };

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={t("page.teammates.kicker")}
          title={t("page.teammates.title")}
          subtitle={t("page.teammates.sub")}
        />

        <div
          className="rounded-2xl bg-zinc-900/50 border border-white/5 p-4 sm:p-5 mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          data-testid="teammates-filters"
        >
          <FilterSelect
            value={filters.game}
            onValueChange={(v) => handleFilterChange({ game: v })}
            placeholder={t("nav.teammates")}
            options={[
              { value: "any", label: t("page.teammates.filter.game") },
              ...games.map((g) => ({ value: g.name, label: g.name })),
            ]}
            testid="filter-game"
          />
          <FilterSelect
            value={filters.country}
            onValueChange={(v) => handleFilterChange({ country: v })}
            placeholder="Country"
            options={[
              { value: "any", label: t("page.teammates.filter.country") },
              ...countries.map((c) => ({ value: c.code, label: `${c.flag} ${c.name}` })),
            ]}
            testid="filter-country"
          />
          <FilterSelect
            value={filters.language}
            onValueChange={(v) => handleFilterChange({ language: v })}
            placeholder="Language"
            options={[
              { value: "any", label: t("page.teammates.filter.language") },
              ...languages.map((l) => ({ value: l.code, label: l.name })),
            ]}
            testid="filter-language"
          />
          <FilterSelect
            value={filters.platform}
            onValueChange={(v) => handleFilterChange({ platform: v })}
            placeholder="Platform"
            options={[
              { value: "any", label: t("page.teammates.filter.platform") },
              ...platforms.map((p) => ({ value: p.code, label: p.name })),
            ]}
            testid="filter-platform"
          />
          <FilterSelect
            value={filters.mode}
            onValueChange={(v) => handleFilterChange({ mode: v })}
            placeholder="Mode"
            options={[
              { value: "any", label: t("page.teammates.filter.mode_any") },
              { value: "ranked", label: t("page.teammates.filter.mode_ranked") },
              { value: "casual", label: t("page.teammates.filter.mode_casual") },
            ]}
            testid="filter-mode"
          />
          <label className="flex items-center justify-between gap-3 rounded-xl bg-zinc-950 border border-white/10 px-3 py-2">
            <span className="text-sm text-zinc-300 inline-flex items-center gap-2">
              <Radio size={14} className="text-neon" /> {t("page.teammates.online_only")}
            </span>
            <Switch
              checked={filters.online_only}
              onCheckedChange={(v) => handleFilterChange({ online_only: v })}
              data-testid="filter-online"
            />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-xl bg-zinc-950 border border-white/10 px-3 py-2">
            <span className="text-sm text-zinc-300 inline-flex items-center gap-2">
              <MessageCircle size={14} className="text-neon" /> {t("page.teammates.mic_only") || "Mic on"}
            </span>
            <Switch
              checked={filters.mic_only}
              onCheckedChange={(v) => handleFilterChange({ mic_only: v })}
              data-testid="filter-mic"
            />
          </label>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-3 text-sm text-zinc-400">
            <UsersIcon size={16} className="text-neon" />
            <span>
              <span className="text-white font-semibold">{teammates.length}</span> {t("page.teammates.pilots_online")} ·{" "}
              <span className="text-neon">{onlineCount}</span> {t("page.teammates.online_count")}
            </span>
          </div>
          <Button
            variant="ghost"
            className="text-neon hover:text-neon hover:bg-neon/10"
            onClick={fetchTeammates}
            data-testid="refresh-teammates"
          >
            {t("page.teammates.refresh")}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && teammates.length === 0 ? (
            <div className="col-span-full text-center text-zinc-500 py-12">{t("page.teammates.loading")}</div>
          ) : teammates.length === 0 ? (
            <div
              className="col-span-full rounded-3xl border border-dashed border-white/10 bg-zinc-900/30 p-10 text-center"
              data-testid="no-teammates"
            >
              <div className="w-14 h-14 rounded-2xl bg-neon/10 border border-neon/25 grid place-items-center text-neon mx-auto">
                <UsersIcon />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold">
                {t("teammates.empty_h") || "No pilots found yet"}
              </h3>
              <p className="mt-2 text-sm text-zinc-400 max-w-sm mx-auto">
                {t("teammates.empty_sub") ||
                  "Be one of the first pilots to build the community."}
              </p>
              <Button
                asChild
                size="sm"
                className="mt-5 rounded-full bg-neon text-black hover:bg-neon-hover font-bold"
                data-testid="teammates-empty-cta"
              >
                <Link to="/app/profile">
                  {t("teammates.empty_cta") || "Complete your profile"}
                </Link>
              </Button>
            </div>
          ) : (
            visibleTeammates.map((tm, i) => {
              const initial = (tm.nickname || "?").slice(0, 1).toUpperCase();
              const langs = tm.languages || [];
              const games = tm.games || [];
              const platforms = tm.platforms || [];
              return (
                <article
                  key={tm.user_id || i}
                  className="card-interactive rounded-2xl border border-white/5 bg-zinc-900/50 backdrop-blur-sm p-5 animate-fade-up"
                  style={{ animationDelay: `${i * 40}ms` }}
                  data-testid={`teammate-${tm.user_id || i}`}
                >
                  <div className="flex items-center gap-3">
                    <Link
                      to={tm.nickname ? `/u/${encodeURIComponent(tm.nickname)}` : "#"}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-neon to-blue-500 grid place-items-center font-black text-black text-lg shadow-inner hover:scale-105 transition-transform"
                      data-testid={`teammate-${tm.user_id || i}-avatar-link`}
                    >
                      {initial}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Link
                          to={tm.nickname ? `/u/${encodeURIComponent(tm.nickname)}` : "#"}
                          className="font-display font-bold truncate hover:text-neon transition-colors"
                          data-testid={`teammate-${tm.user_id || i}-name-link`}
                        >
                          @{tm.nickname}
                        </Link>
                        {tm.online && (
                          <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {tm.country || "—"} · {tm.skill_level || "—"}
                      </div>
                    </div>
                  </div>
                  {langs.length > 0 && (
                    <p className="mt-3 text-sm text-zinc-400 line-clamp-2">
                      {langs.join(" · ")}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {games.slice(0, 3).map((g) => (
                      <Badge key={g} variant="outline" className="bg-white/5 border-white/10 text-zinc-300">
                        {g}
                      </Badge>
                    ))}
                    {platforms.map((p) => (
                      <Badge key={p} variant="outline" className="bg-neon/10 border-neon/20 text-neon uppercase text-[10px]">
                        {p}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    className="mt-4 w-full rounded-full bg-neon text-black hover:bg-neon-hover font-bold"
                    onClick={() => handleInvite(tm)}
                    data-testid={`invite-${tm.user_id || i}`}
                  >
                    <MessageCircle size={14} className="mr-2" />
                    {t("page.teammates.invite")}
                  </Button>
                </article>
              );
            })
          )}
        </div>

        {/* Guest lock — blurred preview of extra pilots + CTA */}
        {isGuest && hiddenCount > 0 && (
          <div
            className="relative mt-8 rounded-3xl overflow-hidden border border-neon/30 bg-gradient-to-br from-neon/10 via-zinc-900/60 to-black"
            data-testid="guest-teammates-lock"
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-6 opacity-40 blur-[3px] pointer-events-none select-none">
              {teammates.slice(GUEST_LIMIT, GUEST_LIMIT + 3).map((tm) => (
                <div key={tm.id} className="rounded-2xl border border-white/10 bg-zinc-900/60 p-5">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full bg-gradient-to-br ${tm.avatar_color} grid place-items-center font-bold text-white`}
                    >
                      {tm.handle.slice(0, 1)}
                    </div>
                    <div>
                      <div className="font-bold">{tm.handle}</div>
                      <div className="text-xs text-zinc-500">{tm.rank}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 grid place-items-center px-4">
              <div className="text-center max-w-md">
                <span className="inline-flex w-12 h-12 rounded-2xl bg-neon/15 border border-neon/30 text-neon items-center justify-center">
                  <Lock size={22} />
                </span>
                <h3 className="mt-4 font-display text-2xl sm:text-3xl font-black">
                  {t("guest.teammates_locked", { count: teammates.length })}
                </h3>
                <p className="mt-2 text-zinc-300 text-sm">{t("guest.teammates_locked_sub")}</p>
                <Link to="/login" data-testid="guest-teammates-cta">
                  <Button className="mt-5 h-12 px-7 rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon">
                    {t("wall.signup")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </PageContainer>
    </AppShell>
  );
}

function FilterSelect({ value, onValueChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className="h-11 rounded-xl bg-zinc-950 border-white/10 focus:ring-neon text-white"
        data-testid={testid}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-white/10 text-white">
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} className="focus:bg-neon/10 focus:text-neon">
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
